import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ProviderWorkerReport } from '../../api/server/models/ProviderWorkerReport';
import { ProviderWorkerReportClaimDetails } from '../../api/server/models/ProviderWorkerReportClaimDetails';
import { ProviderWorkerReportClaimIdPage } from '../../api/server/models/ProviderWorkerReportClaimIdPage';
import { ProviderWorkerReportMetric } from '../../api/server/models/ProviderWorkerReportMetric';
import { ProviderWorkerReportWorkflow } from '../../api/server/models/ProviderWorkerReportWorkflow';
import { IWorkerReportApi, WorkflowClaim } from '../../services/worker-report/worker-report-api';
import {
    actionChips,
    buildCatalog,
    buildHeatmap,
    CLAIM_PAGE_SIZE,
    IActionChip,
    IHeatmap,
    IPagerState,
    IWorkerReportCatalog,
    IWorkerReportFilters,
    IWorkerReportSummary,
    pagerState,
    stepOptions,
    summaryCounts,
} from '../../services/worker-report/worker-report.model';

/** One heatmap box: a status row (every action) or an action row, in one week. */
export interface IWorkflowBox {
    status: string;
    week: number;
    /** Set when the box is on an action row. */
    action?: string;
}

const errorText = (error: unknown, fallback: string): string =>
    error instanceof Error && error.message ? error.message : fallback;

/**
 * The Workflows page's state, on either console (FE-167). What differs between the consoles, the
 * claim endpoint, comes in as `api` (the FE-155 shape). In demo mode `demoApi` answers instead, read
 * live because the provider console turns demo mode on after its stores are built (NS-14).
 *
 * Requests, per the crosswalk:
 *
 * 1. the report, once per page load: `includeClaimIds=false` and no filters, so every week of the last
 *    365 days in one response. Filters, the summary-card filter, expanding a row and expanding a claim
 *    make no request;
 * 2. a page of claim IDs with their workflow details, when a box is clicked, a chip or the pager is
 *    used, or a filter changes while the drawer is open;
 * 3. the claims for those IDs, right after 2.
 *
 * A response for a box or page already left behind is dropped, so a slow page cannot overwrite a newer one.
 */
export class WorkerReportStore {
    @observable.ref public report: ProviderWorkerReport | undefined;
    @observable public loading: boolean = false;
    /** Set when the report could not be read. The page shows "unknown", never zeros (NS-3). */
    @observable public error: string | undefined;

    @observable public workflow: ProviderWorkerReportWorkflow | undefined;
    @observable public step: string | undefined;
    @observable public metric: ProviderWorkerReportMetric | undefined;
    @observable public expanded = new Set<string>();

    /* ---- Drawer: the claims behind one box ------------------------------------------------- */
    @observable.ref public box: IWorkflowBox | undefined;
    /** The chip selected in the drawer. Unset is "All actions"; it starts as the box's own action. */
    @observable public drawerAction: string | undefined;
    @observable public offset: number = 0;
    @observable.ref public page: ProviderWorkerReportClaimIdPage | undefined;
    /** The drill response's own snapshot time: the drawer's "as of". */
    @observable public pageGeneratedAt: string | undefined;
    @observable public pageLoading: boolean = false;
    @observable public pageError: string | undefined;
    /** Claims for the page, by ID. An ID missing here after loading is a claim the endpoint did not return. */
    @observable.ref public claims: Map<string, WorkflowClaim> = new Map();
    @observable public claimsLoading: boolean = false;
    @observable public claimsError: string | undefined;
    @observable public openClaimId: string | undefined;

    private api: IWorkerReportApi;
    private demoApi: IWorkerReportApi | undefined;
    private features: { isDemo: boolean };
    private reportRequestId = 0;
    private pageRequestId = 0;

    constructor({ api, demoApi, features }: { api: IWorkerReportApi; demoApi?: IWorkerReportApi; features: { isDemo: boolean } }) {
        this.api = api;
        this.demoApi = demoApi;
        this.features = features;
        makeObservable(this);
    }

    private get service(): IWorkerReportApi | undefined {
        if (this.features.isDemo) {
            return this.demoApi;
        }

        return this.api;
    }

    @computed public get filters(): IWorkerReportFilters {
        return { workflow: this.workflow, step: this.step, metric: this.metric };
    }

    @computed public get catalog(): IWorkerReportCatalog {
        return buildCatalog(this.report || { workflowStatuses: [], workflowSteps: [], workflowActions: [] });
    }

    /** Undefined until a report has loaded, so the cards render as unknown rather than zero. */
    @computed public get summary(): IWorkerReportSummary | undefined {
        return this.report ? summaryCounts(this.report.groups, this.filters) : undefined;
    }

    @computed public get heatmap(): IHeatmap {
        return this.report ? buildHeatmap(this.report.groups, this.catalog, this.filters) : { rows: [], max: 0 };
    }

    @computed public get steps(): { value: string; label: string }[] {
        return this.report ? stepOptions(this.report.groups, this.catalog) : [];
    }

    @computed public get allExpanded(): boolean {
        const rows = this.heatmap.rows.filter(row => row.actions.length > 0);
        return rows.length > 0 && rows.every(row => this.expanded.has(row.status));
    }

    @computed public get chips(): IActionChip[] {
        return this.report && this.box
            ? actionChips(this.report.groups, this.catalog, this.filters, this.box.status, this.box.week)
            : [];
    }

    @computed public get pager(): IPagerState | undefined {
        return this.page ? pagerState(this.page) : undefined;
    }

    /** Details for the page, by claim ID. */
    @computed public get details(): Map<string, ProviderWorkerReportClaimDetails> {
        return new Map((this.page?.claimStatusDetails || []).map(detail => [detail.claimId, detail]));
    }

    @action public load = async (): Promise<void> => {
        const service = this.service;
        const request = ++this.reportRequestId;
        this.loading = true;
        this.error = undefined;
        if (!service) {
            this.loading = false;
            this.report = undefined;
            this.error = 'Workflows are not generated in demo mode yet.';
            return;
        }
        try {
            // The backend's page-load call (2026-10-14): 200 on production. Sandbox still answers 500 to it,
            // so there the page shows its "counts are unknown" state until the sandbox deploy catches up.
            const report = await service.fetchReport({ includeClaimIds: false });
            runInAction(() => {
                if (request !== this.reportRequestId) {
                    return;
                }
                this.report = report;
                this.loading = false;
                // A step that no longer exists in the new report would filter the grid to nothing.
                if (this.step && !report.groups.some(group => group.workflowStep === this.step)) {
                    this.step = undefined;
                }
            });
        } catch (error) {
            runInAction(() => {
                if (request !== this.reportRequestId) {
                    return;
                }
                this.loading = false;
                this.report = undefined;
                this.error = errorText(error, 'The worker report could not be loaded.');
            });
        }
    };

    /** Forget everything: another account, or leaving the page. */
    @action public reset = (): void => {
        this.reportRequestId++;
        this.report = undefined;
        this.error = undefined;
        this.loading = false;
        this.workflow = undefined;
        this.step = undefined;
        this.metric = undefined;
        this.expanded = new Set();
        this.closeDrawer();
    };

    @action public setWorkflow = (workflow: ProviderWorkerReportWorkflow | undefined): void => {
        this.workflow = workflow;
        this.refreshDrawer();
    };

    @action public setStep = (step: string | undefined): void => {
        this.step = step;
        this.refreshDrawer();
    };

    /** A card click. Clicking the selected card again goes back to Total. */
    @action public setMetric = (metric: ProviderWorkerReportMetric | undefined): void => {
        this.metric = this.metric === metric ? undefined : metric;
        this.refreshDrawer();
    };

    @action public toggleStatus = (status: string): void => {
        const next = new Set(this.expanded);
        if (next.has(status)) {
            next.delete(status);
        } else {
            next.add(status);
        }
        this.expanded = next;
    };

    @action public toggleExpandAll = (): void => {
        this.expanded = this.allExpanded
            ? new Set()
            : new Set(this.heatmap.rows.filter(row => row.actions.length > 0).map(row => row.status));
    };

    @action public openBox = (box: IWorkflowBox): void => {
        this.box = box;
        this.drawerAction = box.action;
        this.openClaimId = undefined;
        void this.loadPage(0);
    };

    @action public setDrawerAction = (actionValue: string | undefined): void => {
        if (!this.box) {
            return;
        }
        this.drawerAction = actionValue;
        this.openClaimId = undefined;
        void this.loadPage(0);
    };

    @action public nextPage = (): void => {
        if (this.pager?.hasNext && this.page) {
            void this.loadPage(this.page.offset + this.page.claimIds.length);
        }
    };

    @action public previousPage = (): void => {
        if (this.pager?.hasPrevious) {
            void this.loadPage(Math.max(0, this.offset - CLAIM_PAGE_SIZE));
        }
    };

    @action public closeDrawer = (): void => {
        this.pageRequestId++;
        this.box = undefined;
        this.drawerAction = undefined;
        this.page = undefined;
        this.pageGeneratedAt = undefined;
        this.pageLoading = false;
        this.pageError = undefined;
        this.claims = new Map();
        this.claimsLoading = false;
        this.claimsError = undefined;
        this.openClaimId = undefined;
        this.offset = 0;
    };

    @action public toggleClaim = (claimId: string): void => {
        this.openClaimId = this.openClaimId === claimId ? undefined : claimId;
    };

    private refreshDrawer(): void {
        if (this.box) {
            this.openClaimId = undefined;
            void this.loadPage(0);
        }
    }

    /** Requests 2 and 3 for the open box at `offset`. */
    @action private loadPage = async (offset: number): Promise<void> => {
        const box = this.box;
        const service = this.service;
        if (!box || !service) {
            return;
        }
        const request = ++this.pageRequestId;
        this.offset = offset;
        this.pageLoading = true;
        this.pageError = undefined;
        this.claimsError = undefined;
        this.claims = new Map();

        let page: ProviderWorkerReportClaimIdPage | undefined;
        try {
            const response = await service.fetchReport({
                workflowStatus: box.status,
                weekBucket: box.week,
                workflowAction: this.drawerAction,
                workflow: this.workflow,
                workflowStep: this.step,
                metric: this.metric,
                includeClaimIds: true,
                includeClaimDetails: true,
                claimIdsLimit: CLAIM_PAGE_SIZE,
                claimIdsOffset: offset,
            });
            page = response.claimIdPage;
            runInAction(() => {
                if (request !== this.pageRequestId) {
                    return;
                }
                this.page = page;
                this.pageGeneratedAt = response.generatedAt;
                this.pageLoading = false;
                if (!page) {
                    this.pageError = 'The report returned no claim list for this box.';
                }
            });
        } catch (error) {
            runInAction(() => {
                if (request !== this.pageRequestId) {
                    return;
                }
                this.page = undefined;
                this.pageLoading = false;
                this.pageError = errorText(error, 'The claims in this box could not be loaded.');
            });
            return;
        }

        if (!page || request !== this.pageRequestId || !page.claimIds.length) {
            return;
        }

        runInAction(() => {
            this.claimsLoading = true;
        });
        try {
            const claims = await service.fetchClaimsByIds(page.claimIds);
            runInAction(() => {
                if (request !== this.pageRequestId) {
                    return;
                }
                this.claims = new Map(claims.filter(claim => claim.id).map(claim => [claim.id as string, claim]));
                this.claimsLoading = false;
            });
        } catch (error) {
            runInAction(() => {
                if (request !== this.pageRequestId) {
                    return;
                }
                this.claimsLoading = false;
                this.claimsError = errorText(error, 'Claim details could not be loaded.');
            });
        }
    };
}
